import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getBillingItems, createBillingItem, updateBillingItemStatus, updateBillingItem, getClients } from '../services/api';
import { DollarSign, Plus, Search, Pencil, ChevronRight, X } from 'lucide-react';

const typeConfig = {
  setup:     { text: 'Paramétrage',  color: 'bg-purple-100 text-purple-700' },
  reception: { text: 'Réception',    color: 'bg-blue-100 text-blue-700' },
  order:     { text: 'Commande',     color: 'bg-orange-100 text-orange-700' },
  other:     { text: 'Autre',        color: 'bg-slate-100 text-slate-700' },
};

const statusConfig = {
  to_invoice: { text: 'A facturer',  color: 'bg-amber-100 text-amber-700' },
  invoiced:   { text: 'Facturé',     color: 'bg-blue-100 text-blue-700' },
  paid:       { text: 'Payé',        color: 'bg-emerald-100 text-emerald-700' },
};

const nextStatus = { to_invoice: 'invoiced', invoiced: 'paid' };

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatAmount = (val) => {
  if (val === null || val === undefined) return null;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);
};

const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    months.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return months;
};

const Billing = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [createForm, setCreateForm] = useState({ type: 'setup', code: '', client_id: '', date: '', amount: '', description: '' });
  const [editForm, setEditForm] = useState({ code: '', amount: '', description: '' });

  const monthOptions = getMonthOptions();

  useEffect(() => {
    loadItems();
    loadClients();
  }, [filterType, filterMonth, filterClient]);

  const loadItems = async () => {
    try {
      const params = {};
      if (filterType) params.type = filterType;
      if (filterMonth) params.month = filterMonth;
      if (filterClient) params.client_id = filterClient;
      const res = await getBillingItems(params);
      setItems(res.data);
    } catch (e) {
      console.error('Erreur:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const res = await getClients();
      setClients(res.data);
    } catch (e) {
      console.error('Erreur:', e);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createBillingItem({
        ...createForm,
        amount: createForm.amount ? parseFloat(createForm.amount) : null,
      });
      setShowCreateModal(false);
      setCreateForm({ type: 'setup', code: '', client_id: '', date: '', amount: '', description: '' });
      loadItems();
    } catch (error) {
      alert('Erreur : ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    try {
      await updateBillingItemStatus(itemId, newStatus);
      loadItems();
    } catch (error) {
      alert('Erreur : ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await updateBillingItem(editItem.id, {
        code: editForm.code,
        amount: editForm.amount !== '' ? parseFloat(editForm.amount) : null,
        description: editForm.description,
      });
      setEditItem(null);
      loadItems();
    } catch (error) {
      alert('Erreur : ' + (error.response?.data?.detail || error.message));
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({ code: item.code || '', amount: item.amount ?? '', description: item.description || '' });
  };

  const filtered = items.filter(item =>
    (item.reference || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sumByStatus = (status) => filtered.filter(i => i.status === status).reduce((s, i) => s + (i.amount || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center min-h-96 text-slate-500">Chargement...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Facturation</h1>
          <p className="text-slate-500">{filtered.length} prestations</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          <Plus size={18} /> Nouvelle prestation
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-4 rounded-xl shadow flex items-center gap-4">
          <DollarSign className="text-amber-500" size={24} />
          <div>
            <div className="text-2xl font-bold text-slate-800">{formatAmount(sumByStatus('to_invoice')) || '0 €'}</div>
            <div className="text-sm text-slate-500">A facturer</div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow flex items-center gap-4">
          <DollarSign className="text-blue-500" size={24} />
          <div>
            <div className="text-2xl font-bold text-slate-800">{formatAmount(sumByStatus('invoiced')) || '0 €'}</div>
            <div className="text-sm text-slate-500">Facturé - en attente</div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-xl shadow flex items-center gap-4">
          <DollarSign className="text-emerald-500" size={24} />
          <div>
            <div className="text-2xl font-bold text-slate-800">{formatAmount(sumByStatus('paid')) || '0 €'}</div>
            <div className="text-sm text-slate-500">Payé</div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-64 flex items-center gap-3 bg-white px-4 py-2.5 rounded-lg border border-slate-200">
          <Search size={18} className="text-slate-400" />
          <input
            type="text"
            placeholder="Rechercher référence, code, client..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 outline-none text-sm"
          />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2.5 border border-slate-200 rounded-lg bg-white text-sm">
          <option value="">Tous les types</option>
          <option value="setup">Paramétrage</option>
          <option value="reception">Réception</option>
          <option value="order">Commande</option>
          <option value="other">Autre</option>
        </select>
        <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="px-3 py-2.5 border border-slate-200 rounded-lg bg-white text-sm">
          <option value="">Toutes les périodes</option>
          {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} className="px-3 py-2.5 border border-slate-200 rounded-lg bg-white text-sm">
          <option value="">Tous les clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Référence</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Code prestation</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Client</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Date</th>
                <th className="px-4 py-3 text-right text-sm font-semibold text-slate-600">Montant</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Statut</th>
                <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item, i) => {
                const typeCfg = typeConfig[item.type] || typeConfig.other;
                const statusCfg = statusConfig[item.status] || statusConfig.to_invoice;
                const next = nextStatus[item.status];
                return (
                  <tr key={item.id} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                    <td className="px-4 py-3 text-sm font-mono text-slate-700">{item.reference}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeCfg.color}`}>{typeCfg.text}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-700">{item.code || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{item.client_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{formatDate(item.date)}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium">
                      {item.amount != null
                        ? <span className="text-slate-800">{formatAmount(item.amount)}</span>
                        : <span className="text-slate-400 italic">A définir</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusCfg.color}`}>{statusCfg.text}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-blue-600 transition-colors" title="Modifier">
                          <Pencil size={16} />
                        </button>
                        {next && (
                          <button
                            onClick={() => handleStatusChange(item.id, next)}
                            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-emerald-600 transition-colors"
                            title={statusConfig[next]?.text}
                          >
                            <ChevronRight size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-12 text-center">
            <DollarSign size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Aucune prestation trouvée</p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-800">Nouvelle prestation</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} className="text-slate-500" /></button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                <select value={createForm.type} onChange={e => setCreateForm({...createForm, type: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" required>
                  <option value="setup">Paramétrage initial</option>
                  <option value="reception">Réception de stock</option>
                  <option value="order">Préparation de commande</option>
                  <option value="other">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Client *</label>
                <select value={createForm.client_id} onChange={e => setCreateForm({...createForm, client_id: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" required>
                  <option value="">Sélectionner un client</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Code prestation</label>
                <input type="text" value={createForm.code} onChange={e => setCreateForm({...createForm, code: e.target.value})} placeholder="Ex: CMD-000001, REC-000001..." className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                  <input type="date" value={createForm.date} onChange={e => setCreateForm({...createForm, date: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Montant (EUR)</label>
                  <input type="number" step="0.01" min="0" value={createForm.amount} onChange={e => setCreateForm({...createForm, amount: e.target.value})} placeholder="0.00" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea value={createForm.description} onChange={e => setCreateForm({...createForm, description: e.target.value})} placeholder="Description optionnelle..." className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" rows={2} />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Créer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditItem(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Modifier la prestation</h2>
                <p className="text-sm text-slate-500">{editItem.reference}</p>
              </div>
              <button onClick={() => setEditItem(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X size={20} className="text-slate-500" /></button>
            </div>
            <form onSubmit={handleEdit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Code prestation</label>
                <input type="text" value={editForm.code} onChange={e => setEditForm({...editForm, code: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Montant (EUR)</label>
                <input type="number" step="0.01" min="0" value={editForm.amount} onChange={e => setEditForm({...editForm, amount: e.target.value})} placeholder="0.00" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" rows={2} />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditItem(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Annuler</button>
                <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
